import React from "react";
import { Scatter } from "react-chartjs-2";
import {
	Chart as ChartJS,
	Tooltip,
	Legend,
	PointElement,
	LinearScale,
	Title,
} from "chart.js";

ChartJS.register(Tooltip, Legend, PointElement, LinearScale, Title);

const Graph = ({ graphData, loadingGraph }) => {
	const riskFreeRate = 0.02;

	//calculate Sharpe Ratios
	const sharpeRatios = graphData
		? graphData.opt_rets.map((ret, index) => {
				const vol = graphData.optimal_vol[index];
				return (ret - riskFreeRate) / vol;
		  })
		: [];

	//normalize Sharpe Ratios
	const maxSharpe = Math.max(...sharpeRatios);
	const minSharpe = Math.min(...sharpeRatios);

	const normalizeSharpe = (sharpe) => {
		return (sharpe - minSharpe) / (maxSharpe - minSharpe);
	};

	//create gradient colors based on Sharpe Ratios
	const gradientColors = sharpeRatios.map((sharpe) => {
		const normalized = normalizeSharpe(sharpe);
		const blueIntensity = 255 * normalized;
		return `rgba(${0}, ${blueIntensity}, 255, 0.8)`;
	});

	//graph data
	const data = graphData
		? {
				datasets: [
					{
						label: "Portfolio Optimization",
						data: graphData.opt_rets.map((y, index) => ({
							x: graphData.optimal_vol[index],
							y: y,
						})),
						backgroundColor: gradientColors,
						borderColor: "rgba(0, 0, 0, 0.1)",
						pointRadius: 7,
						pointHoverRadius: 9, //highlight size
						pointHoverBackgroundColor: "rgba(0, 0, 255, 1)", //highlight color
					},
				],
		  }
		: {
				datasets: [],
		  };

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			tooltip: {
				callbacks: {
					label: (context) => {
						const portfolioIndex = context.dataIndex; // Get the portfolio index
						const weights = graphData.opt_weights[portfolioIndex]; // Get weights for this portfolio
						const stockInfo = graphData.symbols.map((symbol, i) => {
							const weight = weights[i];
							return `${symbol}: Weight ${(weight * 100).toFixed(
								2
							)}%`;
						});

						return [
							`Portfolio Volatility: ${context.raw.x.toFixed(4)}`,
							`Portfolio Return: ${context.raw.y.toFixed(4)}`,
							`Sharpe Ratio: ${sharpeRatios[
								portfolioIndex
							].toFixed(4)}`,
							...stockInfo,
						];
					},
				},
			},
		},
		scales: {
			x: {
				title: {
					display: true,
					text: "Portfolio Volatility",
					font: { size: 14, weight: "bold" },
				},
			},
			y: {
				title: {
					display: true,
					text: "Portfolio Return",
					font: { size: 14, weight: "bold" },
				},
			},
		},
	};

	return (
		<div className="flex items-center justify-center lg:justify-normal sm:gap-1 md:gap-4 h-full lg:mx-0 w-full lg:w-fit relative">
			{/* Graph Section */}
			{!loadingGraph ? (
				<div className="w-[85vw] lg:w-[calc(70vw-6rem)] h-[450px] lg:h-[600px]">
					<Scatter data={data} options={options} />
				</div>
			) : (
				<div className="w-[85vw] lg:w-[calc(70vw-6rem)] h-[450px] lg:h-[600px]">
					<Scatter data={{ datasets: [] }} options={options} />
					<div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-4 text-[#105790] font-medium">
						<svg
							className="text-gray-300 animate-spin w-12 h-12"
							viewBox="0 0 64 64"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
						>
							<path
								d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
								stroke="currentColor"
								stroke-width="5"
								stroke-linecap="round"
								stroke-linejoin="round"
							></path>
							<path
								d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
								stroke="currentColor"
								stroke-width="5"
								stroke-linecap="round"
								stroke-linejoin="round"
								className="text-[#105790]"
							></path>
						</svg>{" "}
						Processing...
					</div>
				</div>
			)}
			{/* Gradient Bar Section */}
			<div className="hidden sm:flex flex-col justify-center items-center py-4">
				<h3 className="font-bold text-sm mb-4 text-center md:hidden block">
					Sharpe
				</h3>
				<div className="flex flex-col items-center justify-between bg-gray-100 rounded-lg shadow-lg border h-[450px] lg:h-[600px] w-8 md:w-16 p-4">
					<h3 className="font-bold text-sm mb-4 text-center hidden md:block">
						Sharpe
					</h3>
					<div
						className="w-4 md:w-8 h-full rounded-full"
						style={{
							background: `linear-gradient(to bottom, rgba(173, 216, 230, 0.8), rgba(0, 191, 255, 0.8), rgba(0, 0, 255, 0.8))`, // Blue gradient
						}}
					/>
					<div className="flex flex-col items-center mt-4 text-xs text-gray-700">
						<span className="font-semibold text-blue-600">
							Max: {maxSharpe.toFixed(2)}
						</span>
						<span className="font-semibold text-blue-400">
							Min: {minSharpe.toFixed(2)}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Graph;
